import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Chip, Container, Typography,
  Paper, CircularProgress, Alert, Slider
} from '@mui/material';
import axios from 'axios';
import { apiUrl } from '../../config/api';
import NetworkGraph, { NetworkNode, NetworkLink } from './NetworkGraph';

type Opportunity = {
  skill: string;
  score: number; // number of mentee goals needing this skill
  mentor: { id: number; name: string; role: string };
  mentee: { id: number; name: string; role: string };
};

const SkillsMentoring: React.FC<{ title?: string }> = ({ title }) => {
  const [ops, setOps] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [minScore, setMinScore] = useState<number>(1);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(apiUrl('/api/analysis/mentoring'));
      setOps(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error(e);
      setError('Failed to load mentoring opportunities');
    } finally {
      setLoading(false);
    }
  };

  const { filtered, byMentee, topMentors, stats, networkNodes, networkLinks } = useMemo(() => {
    const f = ops.filter(o => (o.score ?? 0) >= minScore);
    const byM = new Map<string, Opportunity[]>();
    for (const o of f) {
      const key = `${o.mentee.id}::${o.mentee.name}`;
      if (!byM.has(key)) byM.set(key, []);
      byM.get(key)!.push(o);
    }
    // Simple top mentors by total impact
    const mentorScore = new Map<string, number>();
    for (const o of f) {
      const key = `${o.mentor.id}::${o.mentor.name}`;
      mentorScore.set(key, (mentorScore.get(key) || 0) + o.score);
    }
    const topMent = Array.from(mentorScore.entries())
      .map(([k, s]) => {
        const [idStr, name] = k.split('::');
        return { id: Number(idStr), name, total: s };
      })
      .sort((a, b) => b.total - a.total)
      .slice(0, 8);

    // Build network nodes
    const mentors = Array.from(new Map(f.map(o => [o.mentor.id, o.mentor])).values());
    const mentees = Array.from(new Map(f.map(o => [o.mentee.id, o.mentee])).values());

    const nodes: NetworkNode[] = [
      ...mentors.map(m => ({ id: `mentor-${m.id}`, group: 'mentor', title: m.name })),
      ...mentees.map(m => ({ id: `mentee-${m.id}`, group: 'mentee', title: m.name }))
    ];

    // Build network links
    const links: NetworkLink[] = f.map(o => ({
      source: `mentor-${o.mentor.id}`,
      target: `mentee-${o.mentee.id}`,
      weight: o.score,
      label: o.skill
    }));

    return {
      filtered: f,
      byMentee: byM,
      topMentors: topMent,
      stats: { total: f.length },
      networkNodes: nodes,
      networkLinks: links,
    };
  }, [ops, minScore]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">{title || 'Skills Mentoring'}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ width: 240 }}>
            <Typography variant="caption">Minimum need score: {minScore}</Typography>
            <Slider
              size="small"
              step={1}
              min={1}
              max={5}
              value={minScore}
              onChange={(_, v) => setMinScore(v as number)}
            />
          </Box>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
      )}

      {filtered.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">No mentoring opportunities at this threshold</Typography>
          <Typography variant="body2" color="text.secondary">Lower the slider to see more.</Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', gap: 3 }}>
          {/* Left: Grouped list by mentee */}
          <Paper sx={{ flex: 1, p: 2 }}>
            <Typography variant="h6" gutterBottom>Mentees and Matching Mentors</Typography>
            {Array.from(byMentee.entries()).map(([menteeKey, items]) => {
              const [, menteeName] = menteeKey.split('::');
              return (
                <Card key={menteeKey} sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>{menteeName}</Typography>
                    {items
                      .sort((a,b)=> b.score - a.score || a.skill.localeCompare(b.skill))
                      .map((o, idx) => (
                        <Box key={idx} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Box>
                            <Typography variant="body2">
                              <strong>Skill:</strong> {o.skill}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Need score: {o.score}
                            </Typography>
                          </Box>
                          <Chip label={`Mentor: ${o.mentor.name}`} color="primary" size="small" />
                        </Box>
                      ))}
                  </CardContent>
                </Card>
              );
            })}
          </Paper>

          {/* Right: Summary + mini graph */}
          <Box sx={{ width: 380, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Summary</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Opportunities ≥ {minScore}:</Typography>
                  <Chip size="small" label={stats.total} color="secondary" />
                </Box>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Top Mentors (by impact)</Typography>
                {topMentors.length === 0 && (
                  <Typography variant="body2" color="text.secondary">No mentors for current threshold.</Typography>
                )}
                {topMentors.map(m => (
                  <Box key={m.id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                    <Typography variant="body2">{m.name}</Typography>
                    <Chip label={m.total} size="small" />
                  </Box>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Mentoring Graph</Typography>
                <Box sx={{ height: 300 }}>
                  <NetworkGraph
                    nodes={networkNodes}
                    links={networkLinks}
                    height={300}
                    nodeRadius={() => 14}
                    nodeColor={(d: any) => d.group === 'mentor' ? '#1976d2' : '#ff9800'}
                    nodeLabel={(d: any) => d.title}
                    nodeTooltip={(d: any) => d.title}
                    linkStroke={() => '#9e9e9e'}
                    linkStrokeWidth={(d: any) => Math.max(1, d.weight || 1)}
                    linkStrokeOpacity={() => 0.5}
                    linkCurved={false}
                    linkDistance={() => 80}
                    forceStrength={{
                      charge: -200,
                      link: 0.8,
                      collision: 22,
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}
    </Container>
  );
};

export default SkillsMentoring;